import { useCallback } from "react";

import {
  moderateInputFields,
  useGetCustomisation,
} from "queries/legacy/product-queries";

import { MODERATED_PREFIX } from "./constants";

type ValidateModeratedFieldsType = {
  [key: string]: any;
};

type FormatModeratedFieldValueType = {
  currentStep: number;
};

const hasModeratedFields = (data: ValidateModeratedFieldsType) =>
  Object.entries(data).some(
    ([key, value]) => key.startsWith(MODERATED_PREFIX) && value === true
  );

export const useCreationFlowStep = (props: FormatModeratedFieldValueType) => {
  const { currentStep } = props;
  const { data: customisation } = useGetCustomisation();

  const formatModeratedFieldValue = useCallback(
    (data: ValidateModeratedFieldsType, customisationId: string) => {
      const values = Object.fromEntries(
        Object.entries(data)
          .filter(
            ([key, value]) => key.startsWith(MODERATED_PREFIX) && value === true
          )
          .map(([moderatedKey]) => {
            const fieldName = moderatedKey.replace(MODERATED_PREFIX, "");
            const fieldData = data[fieldName];
            const fieldValue =
              fieldData && typeof fieldData === "object" && "value" in fieldData
                ? fieldData.value
                : "";

            // Check for existing hash in customisation
            const existingField = customisation?.[fieldName];
            const existingHash =
              existingField &&
              typeof existingField === "object" &&
              "moderation_hash" in existingField
                ? existingField.moderation_hash
                : "";

            return [
              fieldName,
              {
                value: fieldValue || "",
                moderation_hash: existingHash?.toString() || "",
              },
            ];
          })
      );

      return { customisationId, values };
    },
    [customisation]
  );

  const moderateFields = async (
    data: ValidateModeratedFieldsType,
    customisationId: string,
    slug: string
  ) => {
    if (!data) {
      throw new Error("No data provided for moderation");
    }

    if (!customisationId) {
      throw new Error("No customisation ID provided for moderation");
    }

    const moderatedData = formatModeratedFieldValue(data, customisationId);

    try {
      return await moderateInputFields({ slug, data: moderatedData });
    } catch (error) {
      console.error("Error during moderation request:", error);
      throw error;
    }
  };

  return {
    moderateFields,
    hasModeratedFields,
  };
};
